<template>
  <v-container fluid class="api-docs">
    <!-- Hero -->
    <v-row class="hero mb-6">
      <v-col cols="12">
        <div class="hero-card">
          <div>
            <div class="text-overline primary--text">{{ $t("Documentation") }}</div>
            <div class="text-h4 font-weight-bold mb-2">
              {{ $t("API Externe Gwassou") }}
            </div>
            <div class="text-body-1 grey--text text--darken-1">
              {{ $t("Gerez vehicules, bons et transactions de votre societe depuis vos systemes externes (ERP, comptabilite, automatisme).") }}
            </div>
          </div>
          <div class="hero-actions mt-4">
            <v-btn
              color="primary"
              depressed
              :to="{ name: 'ApiKeys' }"
            >
              <v-icon left>mdi-key-variant</v-icon>
              {{ $t("Gerer mes cles API") }}
            </v-btn>
            <v-btn
              color="orange darken-2"
              depressed
              dark
              @click="downloadPostman"
            >
              <v-icon left>mdi-download</v-icon>
              {{ $t("Telecharger collection Postman") }}
            </v-btn>
          </div>
        </div>
      </v-col>
    </v-row>

    <!-- Layout: nav + content -->
    <v-row>
      <!-- Sommaire -->
      <v-col cols="12" md="3">
        <v-card outlined class="sticky-nav pa-3">
          <div class="text-subtitle-2 font-weight-bold mb-2">
            {{ $t("Sommaire") }}
          </div>
          <v-list dense nav>
            <v-list-item
              v-for="item in toc"
              :key="item.id"
              :href="`#${item.id}`"
              link
              class="toc-item"
            >
              <v-list-item-icon class="mr-3">
                <v-icon small>{{ item.icon }}</v-icon>
              </v-list-item-icon>
              <v-list-item-title class="text-body-2">
                {{ item.label }}
              </v-list-item-title>
            </v-list-item>
          </v-list>
        </v-card>
      </v-col>

      <!-- Contenu -->
      <v-col cols="12" md="9">
        <!-- 1. Obtenir une cle -->
        <v-card outlined id="obtenir" class="doc-section mb-4">
          <v-card-title>
            <v-icon left color="primary">mdi-numeric-1-circle</v-icon>
            {{ $t("Obtenir une cle API") }}
          </v-card-title>
          <v-card-text class="text-body-1">
            <ol class="pl-4">
              <li>{{ $t("Connectez-vous a Gwassou avec un compte administrateur ou manager de votre societe.") }}</li>
              <li>{{ $t("Dans le menu lateral, cliquez sur") }} <strong>« {{ $t("Cles API") }} »</strong>.</li>
              <li>{{ $t("Cliquez sur") }} <strong>« {{ $t("Generer une nouvelle cle") }} »</strong>, {{ $t("donnez-lui un nom (ex. ERP interne), choisissez une expiration optionnelle.") }}</li>
              <li class="warning--text font-weight-medium">
                {{ $t("Copiez immediatement la cle affichee. Elle ne sera plus jamais visible apres fermeture.") }}
              </li>
            </ol>

            <div class="mt-4 mb-2 text-body-2">
              {{ $t("Format de la cle :") }}
            </div>
            <CodeBlock
              language="text"
              :code="'gwa_<8 caracteres>_<32 caracteres>'"
              :inline="false"
            />
            <div class="mt-2 text-caption grey--text">
              {{ $t("Exemple :") }}
              <code>gwa_a4f9bc12_9k2m7n8r3v6t5y1u4i7o0p9q2w8e3r6t</code>
            </div>
          </v-card-text>
        </v-card>

        <!-- 2. Authentification -->
        <v-card outlined id="auth" class="doc-section mb-4">
          <v-card-title>
            <v-icon left color="primary">mdi-numeric-2-circle</v-icon>
            {{ $t("Authentification") }}
          </v-card-title>
          <v-card-text class="text-body-1">
            <p>{{ $t("Toutes les requetes doivent inclure le header HTTP suivant :") }}</p>
            <CodeBlock
              language="http"
              code="Authorization: Api-Key gwa_<prefix>_<secret>"
            />

            <p class="mt-4 mb-2 text-body-2 font-weight-medium">{{ $t("Exemple cURL :") }}</p>
            <CodeBlock
              language="bash"
              :code="`curl ${apiBase}/vehicles \\\n  -H 'Authorization: Api-Key gwa_a4f9bc12_9k2m7n8r3v6t5y1u4i7o0p9q2w8e3r6t'`"
            />

            <div class="mt-5 text-subtitle-2 font-weight-bold">
              {{ $t("Codes d'erreur") }}
            </div>
            <v-simple-table dense class="mt-2 error-table">
              <thead>
                <tr>
                  <th>Code</th>
                  <th>{{ $t("Cause") }}</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td><v-chip small color="error" text-color="white">401</v-chip></td>
                  <td>{{ $t("Cle absente, invalide, revoquee, expiree, ou createur sans role admin") }}</td>
                </tr>
                <tr>
                  <td><v-chip small color="warning" text-color="white">403</v-chip></td>
                  <td>{{ $t("Cle valide mais privileges insuffisants pour cette operation") }}</td>
                </tr>
                <tr>
                  <td><v-chip small color="orange" text-color="white">429</v-chip></td>
                  <td>{{ $t("Trop de requetes (limite : 120 / minute / cle)") }}</td>
                </tr>
              </tbody>
            </v-simple-table>
          </v-card-text>
        </v-card>

        <!-- 3. Endpoints -->
        <v-card outlined id="endpoints" class="doc-section mb-4">
          <v-card-title>
            <v-icon left color="primary">mdi-numeric-3-circle</v-icon>
            {{ $t("Endpoints CRUD") }}
          </v-card-title>
          <v-card-text>
            <div class="text-body-2 mb-3">
              {{ $t("URL de base :") }} <code>{{ apiBase }}</code>
            </div>

            <!-- Resource selector -->
            <v-tabs v-model="resourceTab" grow class="mb-3">
              <v-tab>
                <v-icon left>mdi-car</v-icon>
                {{ $t("Vehicules") }}
              </v-tab>
              <v-tab>
                <v-icon left>mdi-ticket-percent</v-icon>
                {{ $t("Bons") }}
              </v-tab>
              <v-tab>
                <v-icon left>mdi-swap-horizontal</v-icon>
                {{ $t("Transactions") }}
              </v-tab>
            </v-tabs>

            <v-tabs-items v-model="resourceTab">
              <!-- Vehicules -->
              <v-tab-item>
                <div class="text-body-2 mt-3 mb-2 font-weight-medium">
                  {{ $t("Parametres de filtrage (GET /vehicles) :") }}
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>{{ $t("Parametre") }}</th>
                      <th>{{ $t("Type") }}</th>
                      <th>{{ $t("Description") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in vehicleParams" :key="p.name">
                      <td><code>{{ p.name }}</code></td>
                      <td><v-chip x-small outlined>{{ p.type }}</v-chip></td>
                      <td>{{ p.desc }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <v-expansion-panels accordion multiple class="mt-4">
                  <CrudPanel
                    v-for="op in vehicleCrud"
                    :key="op.id"
                    :method="op.method"
                    :path="op.path"
                    :title="op.title"
                    :description="op.description"
                    :request-body="op.body"
                    :response="op.response"
                    :curl="op.curl"
                    :python="op.python"
                  />
                </v-expansion-panels>
              </v-tab-item>

              <!-- Vouchers -->
              <v-tab-item>
                <div class="text-body-2 mt-3 mb-2 font-weight-medium">
                  {{ $t("Parametres de filtrage (GET /vouchers) :") }}
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>{{ $t("Parametre") }}</th>
                      <th>{{ $t("Type") }}</th>
                      <th>{{ $t("Description") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in voucherParams" :key="p.name">
                      <td><code>{{ p.name }}</code></td>
                      <td><v-chip x-small outlined>{{ p.type }}</v-chip></td>
                      <td>{{ p.desc }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <v-alert type="info" text dense class="mt-3">
                  {{ $t("Par defaut, seuls les bons non expires sont renvoyes (valid_until >= aujourd hui).") }}
                  {{ $t("Si votre societe utilise le workflow d approbation, les bons crees via API y sont egalement soumis.") }}
                </v-alert>

                <v-expansion-panels accordion multiple class="mt-4">
                  <CrudPanel
                    v-for="op in voucherCrud"
                    :key="op.id"
                    :method="op.method"
                    :path="op.path"
                    :title="op.title"
                    :description="op.description"
                    :request-body="op.body"
                    :response="op.response"
                    :curl="op.curl"
                    :python="op.python"
                  />
                </v-expansion-panels>
              </v-tab-item>

              <!-- Transactions -->
              <v-tab-item>
                <div class="text-body-2 mt-3 mb-2 font-weight-medium">
                  {{ $t("Parametres de filtrage :") }}
                </div>
                <v-simple-table dense>
                  <thead>
                    <tr>
                      <th>{{ $t("Parametre") }}</th>
                      <th>{{ $t("Type") }}</th>
                      <th>{{ $t("Description") }}</th>
                    </tr>
                  </thead>
                  <tbody>
                    <tr v-for="p in transactionParams" :key="p.name">
                      <td><code>{{ p.name }}</code></td>
                      <td><v-chip x-small outlined>{{ p.type }}</v-chip></td>
                      <td>{{ p.desc }}</td>
                    </tr>
                  </tbody>
                </v-simple-table>

                <v-expansion-panels accordion multiple class="mt-4">
                  <CrudPanel
                    v-for="op in transactionCrud"
                    :key="op.id"
                    :method="op.method"
                    :path="op.path"
                    :title="op.title"
                    :description="op.description"
                    :response="op.response"
                    :curl="op.curl"
                    :python="op.python"
                  />
                </v-expansion-panels>
              </v-tab-item>
            </v-tabs-items>
          </v-card-text>
        </v-card>

        <!-- 4. Scope -->
        <v-card outlined id="scope" class="doc-section mb-4">
          <v-card-title>
            <v-icon left color="primary">mdi-numeric-4-circle</v-icon>
            {{ $t("Scope des donnees") }}
          </v-card-title>
          <v-card-text class="text-body-1">
            <p>
              {{ $t("La cle est rattachee a un utilisateur admin de votre societe. Toutes les requetes sont automatiquement restreintes a :") }}
            </p>
            <ul class="pl-4">
              <li><strong>{{ $t("votre societe") }}</strong> ({{ $t("jamais de fuite cross-tenant") }})</li>
              <li>
                <strong>{{ $t("le departement de l'admin") }}</strong>
                ({{ $t("et ses sous-departements) si l'admin est lie a un staff avec departement") }}
              </li>
            </ul>
          </v-card-text>
        </v-card>

        <!-- 5. Securite -->
        <v-card outlined id="security" class="doc-section mb-4">
          <v-card-title>
            <v-icon left color="primary">mdi-numeric-5-circle</v-icon>
            {{ $t("Securite") }}
          </v-card-title>
          <v-card-text class="text-body-1">
            <ul class="pl-4">
              <li>{{ $t("Stockez la cle cote serveur uniquement (jamais dans du JavaScript front).") }}</li>
              <li>{{ $t("Utilisez HTTPS uniquement.") }}</li>
              <li>{{ $t("Si vous suspectez une fuite, revoquez immediatement la cle depuis l'interface Cles API.") }}</li>
              <li>{{ $t("Configurez une expiration pour les cles a usage limite (integration ponctuelle, audit, etc.).") }}</li>
              <li>{{ $t("Vous pouvez creer plusieurs cles (une par systeme / environnement) pour faciliter la tracabilite.") }}</li>
            </ul>
          </v-card-text>
        </v-card>

        <!-- 6. Limites -->
        <v-card outlined id="limits" class="doc-section mb-4">
          <v-card-title>
            <v-icon left color="primary">mdi-numeric-6-circle</v-icon>
            {{ $t("Limites techniques") }}
          </v-card-title>
          <v-card-text class="text-body-1">
            <ul class="pl-4">
              <li><strong>120 {{ $t("requetes") }} / minute / {{ $t("cle") }}</strong> ({{ $t("au-dela : 429 Too Many Requests") }})</li>
              <li>{{ $t("Pas de dump complet via ?no_pagination=true sur l'endpoint transactions.") }}</li>
              <li>{{ $t("Tailles de page par defaut : 10 sur vehicules / vouchers, 1000 max sur transactions.") }}</li>
            </ul>
          </v-card-text>
        </v-card>

        <!-- 7. Exemple Python -->
        <v-card outlined id="example" class="doc-section mb-4">
          <v-card-title>
            <v-icon left color="primary">mdi-numeric-7-circle</v-icon>
            {{ $t("Exemple complet en Python") }}
          </v-card-title>
          <v-card-text>
            <CodeBlock language="python" :code="pythonExample" />
          </v-card-text>
        </v-card>
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
import config from "@/app.config"

const CodeBlock = {
  name: "CodeBlock",
  props: {
    code: { type: String, required: true },
    language: { type: String, default: "text" },
    inline: { type: Boolean, default: false },
  },
  data() {
    return { copied: false }
  },
  methods: {
    doCopy() {
      const onDone = () => {
        this.copied = true
        setTimeout(() => { this.copied = false }, 1500)
      }
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(this.code).then(onDone).catch(() => {})
      }
    },
  },
  render(h) {
    return h("div", { class: "code-block" }, [
      h("div", { class: "code-block-header" }, [
        h("span", { class: "code-lang" }, this.language),
        h(
          "button",
          {
            class: "code-copy-btn",
            attrs: { type: "button" },
            on: { click: this.doCopy },
          },
          this.copied ? "✓ Copié" : "Copier"
        ),
      ]),
      h("pre", [h("code", this.code)]),
    ])
  },
}

const CrudPanel = {
  name: "CrudPanel",
  components: { CodeBlock },
  props: {
    method: { type: String, required: true },
    path: { type: String, required: true },
    title: { type: String, required: true },
    description: { type: String, default: "" },
    requestBody: { type: String, default: "" },
    response: { type: String, default: "" },
    curl: { type: String, default: "" },
    python: { type: String, default: "" },
  },
  computed: {
    methodColor() {
      const m = this.method.toUpperCase()
      if (m === "GET") return "#1976d2"
      if (m === "POST") return "#2e7d32"
      if (m === "PATCH" || m === "PUT") return "#ef6c00"
      if (m === "DELETE") return "#c62828"
      return "#616161"
    },
  },
  render(h) {
    const section = (label, lang, code) =>
      code
        ? [
            h(
              "div",
              { class: "text-subtitle-2 font-weight-bold mt-3 mb-1" },
              label
            ),
            h(CodeBlock, { props: { language: lang, code } }),
          ]
        : []

    const content = []
    if (this.description) {
      content.push(
        h(
          "p",
          { class: "text-body-2 grey--text mb-3" },
          this.description
        )
      )
    }
    content.push(...section("Request body (JSON)", "json", this.requestBody))
    content.push(...section("Response", "json", this.response))
    content.push(...section("cURL", "bash", this.curl))
    content.push(...section("Python (requests)", "python", this.python))

    return h("v-expansion-panel", [
      h("v-expansion-panel-header", [
        h(
          "div",
          {
            class: "d-flex align-center",
            style: { gap: "12px", flexWrap: "wrap" },
          },
          [
            h(
              "span",
              {
                class: "method-chip",
                style: { background: this.methodColor },
              },
              this.method
            ),
            h("code", { class: "endpoint-path" }, this.path),
            h("span", { class: "endpoint-desc" }, this.title),
          ]
        ),
      ]),
      h("v-expansion-panel-content", content),
    ])
  },
}

export default {
  name: "ApiDocs",
  components: { CodeBlock, CrudPanel },

  data() {
    return {
      resourceTab: 0,
      toc: [
        { id: "obtenir",   icon: "mdi-key-plus",            label: this.$t("Obtenir une cle") },
        { id: "auth",      icon: "mdi-shield-key",          label: this.$t("Authentification") },
        { id: "endpoints", icon: "mdi-api",                 label: this.$t("Endpoints") },
        { id: "scope",     icon: "mdi-account-group",       label: this.$t("Scope") },
        { id: "security",  icon: "mdi-lock-outline",        label: this.$t("Securite") },
        { id: "limits",    icon: "mdi-speedometer-medium",  label: this.$t("Limites") },
        { id: "example",   icon: "mdi-code-braces",         label: this.$t("Exemple") },
      ],

      vehicleParams: [
        { name: "page",       type: "int",     desc: "Numero de page (defaut 1)" },
        { name: "search",     type: "string",  desc: "Recherche conducteur, matricule, marque, modele" },
        { name: "department", type: "int",     desc: "Filtre departement (incl. sous-departements)" },
        { name: "product",    type: "int",     desc: "Filtre par produit autorise" },
        { name: "is_blocked", type: "boolean", desc: "true / false pour filtrer actifs/bloques" },
        { name: "all",        type: "boolean", desc: "true pour desactiver la pagination" },
        { name: "ordering",   type: "string",  desc: "Ex. -created_at, registration_number" },
      ],

      voucherParams: [
        { name: "page",         type: "int",     desc: "Pagination" },
        { name: "search",       type: "string",  desc: "Recherche sur vehicule, conducteur" },
        { name: "start_date",   type: "date",    desc: "valid_until >= start_date (YYYY-MM-DD)" },
        { name: "end_date",     type: "date",    desc: "valid_until <= end_date" },
        { name: "vehicle",      type: "uuid",    desc: "Filtre par vehicule" },
        { name: "product",      type: "int",     desc: "Filtre par produit" },
        { name: "shop",         type: "int",     desc: "Filtre par station" },
        { name: "department",   type: "int",     desc: "Filtre departement" },
        { name: "show_expired", type: "boolean", desc: "true pour inclure les bons expires" },
      ],

      transactionParams: [
        { name: "page",       type: "int",    desc: "Pagination (forcee, pas de dump complet)" },
        { name: "search",     type: "string", desc: "Recherche multi-champs" },
        { name: "startDate",  type: "date",   desc: "Debut (ISO 8601 ou YYYY-MM-DD)" },
        { name: "endDate",    type: "date",   desc: "Fin" },
        { name: "product",    type: "int",    desc: "Filtre produit" },
        { name: "vehicle",    type: "uuid",   desc: "Filtre vehicule" },
        { name: "shop",       type: "int",    desc: "Filtre station" },
        { name: "department", type: "int",    desc: "Filtre departement" },
      ],

    }
  },

  computed: {
    apiBase() {
      const base = (config.general && config.general.api) || ""
      return `${base.replace(/\/$/, "")}/external/v1`
    },

    sampleKey() {
      return "gwa_a4f9bc12_9k2m7n8r3v6t5y1u4i7o0p9q2w8e3r6t"
    },

    pythonExample() {
      const B = this.apiBase
      const K = this.sampleKey
      return `import requests

BASE = "${B}"
KEY = "${K}"

headers = {"Authorization": f"Api-Key {KEY}"}

# 1) Lister les vehicules actifs
r = requests.get(f"{BASE}/vehicles", headers=headers, params={"is_blocked": "false"})
r.raise_for_status()
data = r.json()
print(f"{data['stats']['total']} vehicules ({data['stats']['active']} actifs)")
for v in data["results"]:
    print(f"  - {v['registration_number']} : {v['driver_first_name']} {v['driver_last_name']}")

# 2) Creer un nouveau vehicule
new_vehicle = {
    "registration_number": "AA-999-XX",
    "make": "Renault",
    "model": "Master",
    "year": 2024,
    "driver_first_name": "Marie",
    "driver_last_name": "Kouassi",
    "driver_phone_number": "+22501020304",
    "department": 5,
    "product": 2,
    "monthly_limit": "75000.00",
}
r = requests.post(f"{BASE}/vehicles", headers=headers, json=new_vehicle)
r.raise_for_status()
vehicle_id = r.json()["id"]
print(f"Vehicule cree : {vehicle_id}")

# 3) Creer un bon pour ce vehicule
new_voucher = {
    "vehicle": vehicle_id,
    "purchase_amount": "50000",
    "purchase_liters": "100",
    "valid_until": "2026-12-31",
    "company_product": 2,
    "company_shop": 7,
    "frequency": "Monthly",
}
r = requests.post(f"{BASE}/vouchers", headers=headers, json=new_voucher)
r.raise_for_status()
voucher = r.json()
print(f"Bon cree #{voucher['id']} - statut : {voucher['status']}")

# 4) Modifier le plafond mensuel du vehicule
r = requests.patch(
    f"{BASE}/vehicles/{vehicle_id}",
    headers=headers,
    json={"monthly_limit": "100000.00"},
)
r.raise_for_status()

# 5) Recuperer les transactions du mois en cours
from datetime import date
today = date.today()
r = requests.get(
    f"{BASE}/transactions",
    headers=headers,
    params={
        "startDate": today.replace(day=1).isoformat(),
        "endDate": today.isoformat(),
    },
)
r.raise_for_status()
data = r.json()
print(f"Total mois : {data['total_amount']:,} XOF ({data['total_quantity']:,} L)")
for tx in data["results"][:5]:
    print(f"  - {tx['created_at'][:10]} | {tx['amount']} XOF | {tx['shop']}")

# 6) Revoquer le bon (suppression)
r = requests.delete(f"{BASE}/vouchers/{voucher['id']}", headers=headers)
print(f"Suppression : {r.status_code}")

# 7) Gestion d'erreurs typique
try:
    r = requests.get(f"{BASE}/vehicles", headers=headers, timeout=10)
    r.raise_for_status()
except requests.HTTPError as e:
    if e.response.status_code == 401:
        print("Cle invalide ou revoquee")
    elif e.response.status_code == 429:
        print("Rate limit atteint - attendre 1 minute")
    else:
        print(f"Erreur HTTP {e.response.status_code} : {e.response.text}")
except requests.Timeout:
    print("Timeout - reseau lent")`
    },

    vehicleCrud() {
      const B = this.apiBase
      const K = this.sampleKey
      return [
        {
          id: "v-list",
          method: "GET",
          path: "/vehicles",
          title: this.$t("Liste paginee des vehicules"),
          description: this.$t("Renvoie la liste des vehicules de votre societe avec stats agregees."),
          response: this.fmt({
            count: 12,
            next: B + "/vehicles?page=2",
            previous: null,
            results: [{
              id: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
              registration_number: "AA-123-BB",
              make: "Toyota",
              model: "Hilux",
              year: 2023,
              driver_first_name: "Jean",
              driver_last_name: "Dupont",
              driver_phone_number: "+22507000000",
              department: 5,
              product: 2,
              monthly_limit: "50000.00",
              is_blocked: false,
              created_at: "2026-03-12T10:14:23Z",
            }],
            total_count: 12,
            stats: { total: 12, active: 10, blocked: 2, total_kilometers: 234567 },
          }),
          curl: `curl '${B}/vehicles?is_blocked=false&page=1' \\\n  -H 'Authorization: Api-Key ${K}'`,
          python: `r = requests.get(\n    "${B}/vehicles",\n    headers={"Authorization": "Api-Key ${K}"},\n    params={"is_blocked": "false", "page": 1},\n)\nr.raise_for_status()\nprint(r.json()["stats"])`,
        },
        {
          id: "v-create",
          method: "POST",
          path: "/vehicles",
          title: this.$t("Creer un vehicule"),
          description: this.$t("Cree un vehicule dans votre societe. Le matricule doit etre unique."),
          body: this.fmt({
            registration_number: "AA-123-BB",
            make: "Toyota",
            model: "Hilux",
            year: 2023,
            color: "Blanc",
            vin: "1HGBH41JXMN109186",
            driver_first_name: "Jean",
            driver_last_name: "Dupont",
            driver_phone_number: "+22507000000",
            department: 5,
            product: 2,
            monthly_limit: "50000.00",
            vehicle_type: "TRUCK",
            reservoir_capacity: 80,
            is_kmh: true,
            kmh: 12450,
          }),
          response: this.fmt({
            id: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
            registration_number: "AA-123-BB",
            created_at: "2026-05-05T08:00:00Z",
          }),
          curl: `curl -X POST '${B}/vehicles' \\\n  -H 'Authorization: Api-Key ${K}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{\n    "registration_number": "AA-123-BB",\n    "make": "Toyota",\n    "model": "Hilux",\n    "department": 5,\n    "product": 2\n  }'`,
          python: `payload = {\n    "registration_number": "AA-123-BB",\n    "make": "Toyota",\n    "model": "Hilux",\n    "department": 5,\n    "product": 2,\n}\nr = requests.post(\n    "${B}/vehicles",\n    headers={"Authorization": "Api-Key ${K}"},\n    json=payload,\n)\nprint(r.status_code, r.json())`,
        },
        {
          id: "v-detail",
          method: "GET",
          path: "/vehicles/<uuid>",
          title: this.$t("Voir un vehicule"),
          description: this.$t("Detail complet d un vehicule par son UUID."),
          response: this.fmt({
            id: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
            registration_number: "AA-123-BB",
            make: "Toyota",
            model: "Hilux",
            driver_first_name: "Jean",
            driver_last_name: "Dupont",
            current_spending: "12340.50",
            monthly_limit: "50000.00",
            is_blocked: false,
            company: { id: 1, name: "ACME" },
          }),
          curl: `curl '${B}/vehicles/9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12' \\\n  -H 'Authorization: Api-Key ${K}'`,
          python: `r = requests.get(\n    f"${B}/vehicles/{vehicle_id}",\n    headers={"Authorization": "Api-Key ${K}"},\n)\nprint(r.json())`,
        },
        {
          id: "v-update",
          method: "PATCH",
          path: "/vehicles/<uuid>",
          title: this.$t("Modifier un vehicule"),
          description: this.$t("Mise a jour partielle. N envoyez que les champs a modifier."),
          body: this.fmt({
            monthly_limit: "75000.00",
            driver_phone_number: "+22501010101",
          }),
          response: this.fmt({
            id: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
            monthly_limit: "75000.00",
            driver_phone_number: "+22501010101",
            updated_at: "2026-05-05T09:14:00Z",
          }),
          curl: `curl -X PATCH '${B}/vehicles/9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12' \\\n  -H 'Authorization: Api-Key ${K}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"monthly_limit": "75000.00"}'`,
          python: `r = requests.patch(\n    f"${B}/vehicles/{vehicle_id}",\n    headers={"Authorization": "Api-Key ${K}"},\n    json={"monthly_limit": "75000.00"},\n)\nprint(r.json())`,
        },
        {
          id: "v-delete",
          method: "DELETE",
          path: "/vehicles/<uuid>",
          title: this.$t("Supprimer un vehicule"),
          description: this.$t("Suppression definitive. Impossible si le vehicule a des activites liees."),
          response: this.fmt({ status: 204, body: "(empty)" }),
          curl: `curl -X DELETE '${B}/vehicles/9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12' \\\n  -H 'Authorization: Api-Key ${K}'`,
          python: `r = requests.delete(\n    f"${B}/vehicles/{vehicle_id}",\n    headers={"Authorization": "Api-Key ${K}"},\n)\nprint(r.status_code)  # 204`,
        },
      ]
    },

    voucherCrud() {
      const B = this.apiBase
      const K = this.sampleKey
      return [
        {
          id: "vo-list",
          method: "GET",
          path: "/vouchers",
          title: this.$t("Liste paginee des bons"),
          description: this.$t("Bons d achat de la societe. Par defaut : non expires uniquement."),
          response: this.fmt({
            count: 8,
            results: [{
              id: 142,
              vehicle: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
              purchase_amount: "50000.00",
              purchase_liters: "100.00",
              remaining_amount: "30000.00",
              liter_remaining: "60.00",
              valid_until: "2026-12-31",
              company_product: 2,
              company_shop: 7,
              is_activated: true,
              is_all_used: false,
              status: "approved",
              created_at: "2026-05-01T08:00:00Z",
            }],
            total_count: 8,
            stats: { total_count: 8, total_amount: 400000, total_liters: 800 },
          }),
          curl: `curl '${B}/vouchers?vehicle=9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12' \\\n  -H 'Authorization: Api-Key ${K}'`,
          python: `r = requests.get(\n    "${B}/vouchers",\n    headers={"Authorization": "Api-Key ${K}"},\n    params={"vehicle": vehicle_id},\n)\nprint(r.json()["stats"])`,
        },
        {
          id: "vo-create",
          method: "POST",
          path: "/vouchers",
          title: this.$t("Creer un bon"),
          description: this.$t("Creation soumise au workflow d approbation si la societe l exige."),
          body: this.fmt({
            vehicle: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
            purchase_amount: "50000.00",
            purchase_liters: "100.00",
            valid_until: "2026-12-31",
            company_product: 2,
            company_shop: 7,
            frequency: "Monthly",
            is_all_money: false,
            allow_successive: true,
            accumulate_remaining: false,
            destination: "Mission Abidjan",
          }),
          response: this.fmt({
            id: 143,
            vehicle: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
            purchase_amount: "50000.00",
            purchase_liters: "100.00",
            valid_until: "2026-12-31",
            status: "pending",
            is_activated: false,
            created_at: "2026-05-05T09:30:00Z",
          }),
          curl: `curl -X POST '${B}/vouchers' \\\n  -H 'Authorization: Api-Key ${K}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{\n    "vehicle": "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",\n    "purchase_amount": "50000",\n    "purchase_liters": "100",\n    "valid_until": "2026-12-31",\n    "company_product": 2,\n    "company_shop": 7\n  }'`,
          python: `payload = {\n    "vehicle": "9b1f8e0e-...",\n    "purchase_amount": "50000",\n    "purchase_liters": "100",\n    "valid_until": "2026-12-31",\n    "company_product": 2,\n    "company_shop": 7,\n}\nr = requests.post(\n    "${B}/vouchers",\n    headers={"Authorization": "Api-Key ${K}"},\n    json=payload,\n)\nprint(r.status_code, r.json())`,
        },
        {
          id: "vo-detail",
          method: "GET",
          path: "/vouchers/<id>",
          title: this.$t("Voir un bon"),
          description: this.$t("Detail d un bon par son ID numerique."),
          response: this.fmt({
            id: 143,
            vehicle: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
            purchase_amount: "50000.00",
            remaining_amount: "30000.00",
            valid_until: "2026-12-31",
            status: "approved",
            approved_by: "admin@example.com",
            approved_at: "2026-05-05T09:31:00Z",
          }),
          curl: `curl '${B}/vouchers/143' \\\n  -H 'Authorization: Api-Key ${K}'`,
          python: `r = requests.get(f"${B}/vouchers/{voucher_id}", headers={"Authorization": "Api-Key ${K}"})\nprint(r.json())`,
        },
        {
          id: "vo-update",
          method: "PATCH",
          path: "/vouchers/<id>",
          title: this.$t("Modifier un bon"),
          description: this.$t("Mise a jour partielle (ex. prolonger la validite, changer la station)."),
          body: this.fmt({
            valid_until: "2027-03-31",
            company_shop: 9,
          }),
          response: this.fmt({
            id: 143,
            valid_until: "2027-03-31",
            company_shop: 9,
            updated_at: "2026-05-05T10:00:00Z",
          }),
          curl: `curl -X PATCH '${B}/vouchers/143' \\\n  -H 'Authorization: Api-Key ${K}' \\\n  -H 'Content-Type: application/json' \\\n  -d '{"valid_until": "2027-03-31"}'`,
          python: `r = requests.patch(\n    f"${B}/vouchers/{voucher_id}",\n    headers={"Authorization": "Api-Key ${K}"},\n    json={"valid_until": "2027-03-31"},\n)\nprint(r.json())`,
        },
        {
          id: "vo-delete",
          method: "DELETE",
          path: "/vouchers/<id>",
          title: this.$t("Supprimer un bon"),
          description: this.$t("Suppression definitive du bon."),
          response: this.fmt({ status: 204, body: "(empty)" }),
          curl: `curl -X DELETE '${B}/vouchers/143' \\\n  -H 'Authorization: Api-Key ${K}'`,
          python: `r = requests.delete(f"${B}/vouchers/{voucher_id}", headers={"Authorization": "Api-Key ${K}"})\nprint(r.status_code)`,
        },
      ]
    },

    transactionCrud() {
      const B = this.apiBase
      const K = this.sampleKey
      return [
        {
          id: "t-list",
          method: "GET",
          path: "/transactions",
          title: this.$t("Liste paginee des transactions"),
          description: this.$t("Lecture seule. La pagination est obligatoire (pas de dump complet)."),
          response: this.fmt({
            count: 1245,
            next: B + "/transactions?page=2",
            results: [{
              id: 98765,
              transaction_number: "TX-202605-00012",
              amount: "12500.00",
              quantity: "25.00",
              product: "Diesel",
              shop: "Total Cocody",
              created_at: "2026-05-04T17:32:11Z",
              vehicle: {
                id: "9b1f8e0e-2c3a-4f6b-9d10-7e8c5a4b3d12",
                registration_number: "AA-123-BB",
                driver_name: "Jean Dupont",
              },
              status: "Effectue",
            }],
            total_amount: 8765432,
            total_quantity: 17234,
            total_by_product: [
              { name: "Diesel", total: 5234567, total_liters: 10456 },
              { name: "Essence", total: 3530865, total_liters: 6778 },
            ],
          }),
          curl: `curl '${B}/transactions?startDate=2026-05-01&endDate=2026-05-31' \\\n  -H 'Authorization: Api-Key ${K}'`,
          python: `from datetime import date\ntoday = date.today()\nr = requests.get(\n    "${B}/transactions",\n    headers={"Authorization": "Api-Key ${K}"},\n    params={\n        "startDate": today.replace(day=1).isoformat(),\n        "endDate": today.isoformat(),\n    },\n)\nprint(r.json()["total_amount"])`,
        },
      ]
    },
  },

  methods: {
    fmt(obj) {
      return JSON.stringify(obj, null, 2)
    },

    downloadPostman() {
      const collection = this.buildPostmanCollection()
      const blob = new Blob([JSON.stringify(collection, null, 2)], {
        type: "application/json",
      })
      const url = URL.createObjectURL(blob)
      const a = document.createElement("a")
      a.href = url
      a.download = "gwassou-external-api.postman_collection.json"
      document.body.appendChild(a)
      a.click()
      document.body.removeChild(a)
      URL.revokeObjectURL(url)
    },

    buildPostmanCollection() {
      const baseUrl = this.apiBase
      const auth = {
        type: "apikey",
        apikey: [
          { key: "key", value: "Authorization", type: "string" },
          { key: "value", value: "Api-Key {{api_key}}", type: "string" },
          { key: "in", value: "header", type: "string" },
        ],
      }

      const buildRequest = (op, folder) => {
        const fullPath = baseUrl + op.path
        const item = {
          name: `${op.method} ${op.path} — ${op.title}`,
          request: {
            method: op.method,
            header: [
              { key: "Authorization", value: "Api-Key {{api_key}}", type: "text" },
            ],
            url: {
              raw: fullPath,
              host: [baseUrl.replace(/^https?:\/\//, "").split("/")[0]],
              path: fullPath
                .replace(/^https?:\/\/[^/]+\//, "")
                .split("/")
                .filter(Boolean),
            },
          },
        }
        if (op.body) {
          item.request.header.push({
            key: "Content-Type", value: "application/json", type: "text",
          })
          item.request.body = {
            mode: "raw",
            raw: op.body,
            options: { raw: { language: "json" } },
          }
        }
        if (op.response) {
          item.response = [{
            name: "Example",
            originalRequest: item.request,
            status: op.method === "DELETE" ? "No Content" : "OK",
            code: op.method === "DELETE" ? 204 : 200,
            _postman_previewlanguage: "json",
            header: [{ key: "Content-Type", value: "application/json" }],
            cookie: [],
            body: op.response,
          }]
        }
        return item
      }

      return {
        info: {
          name: "Gwassou - API Externe",
          description: "API externe Gwassou pour gerer vehicules, bons et transactions de votre societe via cle API.",
          schema: "https://schema.getpostman.com/json/collection/v2.1.0/collection.json",
          _postman_id: "gwassou-external-api",
        },
        auth,
        variable: [
          { key: "base_url", value: baseUrl, type: "string" },
          { key: "api_key", value: "REMPLACE_PAR_TA_CLE", type: "string" },
        ],
        item: [
          {
            name: "Vehicules",
            item: this.vehicleCrud.map(op => buildRequest(op, "Vehicules")),
          },
          {
            name: "Bons (Vouchers)",
            item: this.voucherCrud.map(op => buildRequest(op, "Vouchers")),
          },
          {
            name: "Transactions",
            item: this.transactionCrud.map(op => buildRequest(op, "Transactions")),
          },
        ],
      }
    },
  },
}
</script>

<style scoped>
.api-docs {
  max-width: 1400px;
  margin: 0 auto;
}

.hero-card {
  background: linear-gradient(135deg, #f5f7fa 0%, #ffffff 60%);
  border: 1px solid rgba(25, 118, 210, 0.15);
  border-radius: 12px;
  padding: 32px;
}
.hero-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

::v-deep .v-expansion-panel-header {
  font-size: 14px;
}
::v-deep .v-expansion-panel-content__wrap {
  padding-top: 8px;
}

.sticky-nav {
  position: sticky;
  top: 80px;
  max-height: calc(100vh - 100px);
  overflow-y: auto;
}

.toc-item {
  border-radius: 6px;
}

.doc-section {
  scroll-margin-top: 80px;
}

.doc-section .v-card__title {
  background: #fafafa;
  border-bottom: 1px solid #eee;
  font-size: 18px;
  font-weight: 600;
}

/* Code block */
::v-deep .code-block {
  background: #1e1e1e;
  border-radius: 6px;
  margin: 8px 0;
  overflow: hidden;
}
::v-deep .code-block-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 6px 12px;
  background: #2d2d2d;
  color: #aaa;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.5px;
}
::v-deep .code-block .code-lang {
  font-weight: 600;
  color: #82aaff;
}
::v-deep .code-block .code-copy-btn {
  background: transparent;
  color: #ccc;
  border: 1px solid #555;
  padding: 2px 10px;
  border-radius: 4px;
  font-size: 11px;
  cursor: pointer;
  font-family: inherit;
}
::v-deep .code-block .code-copy-btn:hover {
  background: #444;
  color: #fff;
}
::v-deep .code-block pre {
  margin: 0;
  padding: 14px 16px;
  color: #e0e0e0;
  font-family: "Courier New", "Consolas", monospace;
  font-size: 13px;
  overflow-x: auto;
  white-space: pre;
}

/* Endpoint row */
::v-deep .endpoint-row {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 8px 12px;
  background: #f9f9f9;
  border: 1px solid #eee;
  border-radius: 6px;
  margin-bottom: 6px;
  flex-wrap: wrap;
}
::v-deep .method-chip {
  display: inline-block;
  padding: 3px 10px;
  border-radius: 4px;
  color: white;
  font-weight: 700;
  font-size: 12px;
  font-family: monospace;
  min-width: 60px;
  text-align: center;
}
::v-deep .endpoint-path {
  font-family: monospace;
  font-size: 13px;
  color: #1976d2;
  background: transparent;
}
::v-deep .endpoint-desc {
  color: #757575;
  font-size: 13px;
  margin-left: auto;
}

/* Tables */
.error-table th,
.error-table td {
  padding: 8px 12px !important;
}

code {
  background: #f5f5f5;
  color: #c62828;
  padding: 2px 6px;
  border-radius: 3px;
  font-family: "Courier New", monospace;
  font-size: 12.5px;
}
</style>
